import { CallMethodEnums } from '@/types/api';
import {
  EvalCreateReq,
  EvalCreateRes,
  EvalRunsGetRes,
  EvalRunsResultsGetRes,
  EvalsGetRes,
  EvalsSearchParams,
  EvalsSearchRes,
  Evaluation,
  EvaluationDeprecateReq,
  Example,
  MetricCloneReq,
  MetricConfigGetRes,
  MetricCreateReq,
  MetricCreateRes,
  MetricGetRes,
  MetricsGetRes,
  MetricsSearchParams,
  MetricsSearchRes,
  MetricVersionCreateReq,
  MetricVersionDeprecateReq,
  RunCreateReq,
  RunCreateRes,
  SourceCreateReq,
  SourceCreateRes,
  SourceGetRes,
  SourcePreviewGetRes,
  SourcesGetRes,
  SourcesSearchParams,
  SourcesSearchRes,
  SourceUpdateReq,
  SourceUpdateRes,
  SourceValidateRes,
  SourceVersionDeprecateReq
} from '@/types/evals';

import { callJson, executeFetch } from './utils';

export const exampleGet = async( sourceId: string, version: number | 'latest', exampleId: string ): Promise<Example[]> => {
  return await callJson<unknown, Example[]>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/api/proxy/evals/sources/${ sourceId }/examples`,
    params: {
      version: version.toString(),
      example_ids: exampleId
    }
  } );
};

export const evalCreate = async( args: EvalCreateReq ): Promise<EvalCreateRes> => {
  return await callJson<EvalCreateReq, EvalCreateRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/evals/evaluations/'
  } );
};

export const evalRunsGet = async( evaluationId: string, includeDetails?: boolean ): Promise<EvalRunsGetRes> => {
  let route = `/api/evals/evaluation/${ evaluationId }/runs`;

  if( includeDetails ) route += `?include_details=${ includeDetails }`;

  return await callJson<unknown, EvalRunsGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route
  } );
};

export const evalRunsResultsGet = async( evaluationId: string, runId: string, includeDetails?: boolean ): Promise<EvalRunsResultsGetRes> => {
  let route = `/api/evals/evaluation/${ evaluationId }/runs/${ runId }/results`;

  if( includeDetails ) route += `?include_details=${ includeDetails }`;

  return await callJson<unknown, EvalRunsResultsGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route
  } );
};

export const evalsGet = async(): Promise<EvalsGetRes> => {
  return await callJson<unknown, EvalsGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/api/evals/evaluations/'
  } );
};

export const evaluationsSearch = async( params: EvalsSearchParams ): Promise<EvalsSearchRes> => {
  return await callJson<unknown, EvalsSearchRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/api/evals/evaluations/search',
    params
  } );
};

export const evaluationDeprecate = async( evaluationId: string, args: EvaluationDeprecateReq ): Promise<Evaluation> => {
  return await callJson<EvaluationDeprecateReq, Evaluation>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.PATCH,
    route: `/api/evals/evaluations/${ evaluationId }`
  } );
};

export const metricCreate = async( args: MetricCreateReq ): Promise<MetricCreateRes> => {
  return await callJson<MetricCreateReq, MetricCreateRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/evals/metric_providers/'
  } );
};

export const metricConfigGet = async( metricId: string, version: number ): Promise<MetricConfigGetRes> => {
  return await callJson<unknown, MetricConfigGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/api/evals/metric_providers/${ metricId }/version/${ version }`
  } );
};

export const metricsGet = async(): Promise<MetricsGetRes> => {
  return await callJson<unknown, MetricsGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/api/evals/metric_providers/'
  } );
};

export const metricsSearch = async( params: MetricsSearchParams ): Promise<MetricsSearchRes> => {
  return await callJson<unknown, MetricsSearchRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/api/evals/metric_providers/search',
    params
  } );
};

export const metricGet = async( metricId: string ): Promise<MetricGetRes> => {
  return await callJson<unknown, MetricGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/api/evals/metric_providers/${ metricId }`
  } );
};

export const metricVersionDeprecate = async( metricId: string, version: number, body: MetricVersionDeprecateReq ): Promise<MetricConfigGetRes> => {
  return await callJson<MetricVersionDeprecateReq, MetricConfigGetRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.PATCH,
    route: `/api/evals/metric_providers/${ metricId }/version/${ version }`
  } );
};

export const metricVersionCreate = async( metricId: string, body: MetricVersionCreateReq ): Promise<MetricConfigGetRes> => {
  return await callJson<MetricVersionCreateReq, MetricConfigGetRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.PUT,
    route: `/api/evals/metric_providers/${ metricId }`
  } );
};

export const metricClone = async( metricId: string, version: string, body: MetricCloneReq ): Promise<MetricCreateRes> => {
  return await callJson<MetricCloneReq, MetricCreateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/api/evals/metric_providers/${ metricId }/version/${ version }/clone`
  } );
};

export const runCreate = async( evaluationId: string, body: RunCreateReq ): Promise<RunCreateRes> => {
  return await callJson<RunCreateReq, RunCreateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/api/evals/evaluations/${ evaluationId }/runs`
  } );
};

export const sourceCreate = async( args: SourceCreateReq ): Promise<SourceCreateRes> => {
  return await callJson<SourceCreateReq, SourceCreateRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/proxy/evals/sources'
  } );
};

export const sourceGet = async( sourceId: string, version: number | 'latest' ): Promise<SourceGetRes> => {
  return await callJson<unknown, SourceGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/api/proxy/evals/sources/${ sourceId }`,
    params: {
      version: version.toString()
    }
  } );
};

export const sourcePreviewGet = async( sourceId: string, count?: number ): Promise<SourcePreviewGetRes> => {
  let route = `/api/proxy/evals/sources/${ sourceId }/preview`;

  if( count ) route += `?count=${ count }`;

  return await callJson<unknown, SourcePreviewGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route
  } );
};

export const sourcesGet = async(): Promise<SourcesGetRes> => {
  return await callJson<unknown, SourcesGetRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/api/proxy/evals/sources/'
  } );
};

export const sourceValidate = async( args: SourceCreateReq ): Promise<SourceValidateRes> => {
  return await callJson<SourceCreateReq, SourceValidateRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/proxy/evals/sources/validate'
  } );
};

export const sourcesSearch = async( params: SourcesSearchParams): Promise<SourcesSearchRes> => {
  return await callJson<unknown, SourcesSearchRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/api/proxy/evals/sources/search',
    params
  } );
};

export const sourceVersionDeprecate = async( sourceId: string, version: number | 'latest', args: SourceVersionDeprecateReq ): Promise<SourceGetRes> => {
  return await callJson<SourceVersionDeprecateReq, SourceGetRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.PATCH,
    route: `/api/proxy/evals/sources/${ sourceId }/version/${ version.toString() }`
  } );
};

export const createNewSourceVersion = async( sourceId: string, args: SourceUpdateReq ): Promise<SourceUpdateRes> => {
  return await callJson<SourceUpdateReq, SourceUpdateRes>( {
    body: args,
    isProtected: true,
    method: CallMethodEnums.PUT,
    route: `/api/proxy/evals/sources/${ sourceId }`
  } );
};

export const runProgressStream = async( runId: string, signal?: AbortSignal ): Promise<ReadableStream<Uint8Array>> => {
  const response = await executeFetch( {
    route: `/api/proxy/evals/runs/${ runId }/progress/stream`,
    method: CallMethodEnums.GET,
    signal,
    isProtected: true,
    params: { stream: 'true' }
  } );

  if( !response.ok ) throw new Error( `HTTP error! status: ${ response.status }` );
  if( !response.body ) throw new Error( 'No response body' );

  return response.body;
};
